import { forwardRef, useCallback, useEffect, useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import { addMonths, subDays, subMonths, startOfDay } from "date-fns";
import { CalendarHeader } from "./CalendarHeader";
import { CalendarGrid } from "./CalendarGrid";
import { CalendarChips } from "./CalendarChips";
import type { DualCalendarProps } from "./types";

export const DualCalendar = forwardRef<any, DualCalendarProps>(
  (
    {
      locale = "enUS",
      onChange,
      startDate,
      endDate,
      contextMenuMaxHeight,
      topContent,
      bottomContent,
      chips,
      activeChip,
      onChipSelect,
      testID,
      minDate,
      maxDate,
      firstDayOfWeek = 0,
      month,
      initialMonth,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });

    const [leftMonth, setLeftMonth] = useState<Date>(
      (month || initialMonth || startDate || new Date()) as Date
    );
    const [selectedStartDate, setSelectedStartDate] = useState<
      Date | null | undefined
    >(startDate);
    const [selectedEndDate, setSelectedEndDate] = useState<
      Date | null | undefined
    >(endDate);
    const [selectingRange, setSelectingRange] = useState<Date | null>(null);

    const rightMonth = addMonths(leftMonth, 1);

    useEffect(() => {
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
      if (month !== undefined) {
        setLeftMonth(month);
      }
    }, [startDate, endDate, month]);

    const handleDayPress = useCallback(
      (date: Date) => {
        const newDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );

        if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
          setSelectedStartDate(newDate);
          setSelectedEndDate(null);
          setSelectingRange(newDate);
          onChange?.([newDate, null]);
        } else {
          const start =
            selectedStartDate < newDate ? selectedStartDate : newDate;
          const end = selectedStartDate < newDate ? newDate : selectedStartDate;
          setSelectedStartDate(start);
          setSelectedEndDate(end);
          setSelectingRange(null);
          onChange?.([start, end]);
        }
      },
      [onChange, selectedStartDate, selectedEndDate]
    );

    const decreaseLeftMonth = useCallback(
      () => setLeftMonth((prev) => subMonths(prev, 1)),
      []
    );
    const increaseLeftMonth = useCallback(
      () => setLeftMonth((prev) => addMonths(prev, 1)),
      []
    );
    const changeLeftMonth = useCallback(
      (m: number) => setLeftMonth((prev) => new Date(prev.getFullYear(), m, 1)),
      []
    );
    const changeLeftYear = useCallback(
      (y: number) => setLeftMonth((prev) => new Date(y, prev.getMonth(), 1)),
      []
    );

    const decreaseRightMonth = useCallback(
      () => setLeftMonth((prev) => subMonths(prev, 1)),
      []
    );
    const increaseRightMonth = useCallback(
      () => setLeftMonth((prev) => addMonths(prev, 1)),
      []
    );
    const changeRightMonth = useCallback(
      (m: number) => setLeftMonth(new Date(rightMonth.getFullYear(), m - 1, 1)),
      [rightMonth]
    );
    const changeRightYear = useCallback(
      (y: number) => setLeftMonth(new Date(y, rightMonth.getMonth() - 1, 1)),
      [rightMonth]
    );

    const handleChipSelect = useCallback(
      (value: string | null) => {
        onChipSelect?.(value);

        const chip = chips?.find((c) => c.value === value);
        if (!chip || chip.days === undefined) return;

        const today = startOfDay(new Date());

        if (chip.days === 0) {
          setSelectedStartDate(today);
          setSelectedEndDate(today);
          setSelectingRange(null);
          setLeftMonth(today);
          onChange?.([today, today]);
          return;
        }

        const start = subDays(today, chip.days);
        setSelectedStartDate(start);
        setSelectedEndDate(today);
        setSelectingRange(null);
        setLeftMonth(start);
        onChange?.([start, today]);
      },
      [chips, onChange, onChipSelect]
    );

    return (
      <Box
        ref={ref}
        testID={testID}
        backgroundColor={theme.colors.layer.float}
        borderRadius={theme.shape.contextMenu.md.borderRadius}
        padding={16}
        overflow="hidden"
        style={{
          boxShadow:
            "0px 2px 3px 0px rgba(7, 7, 8, 0.2), 0px 6px 10px 4px rgba(7, 7, 8, 0.1)",
          width: 624,
        }}
      >
        {topContent?.({ close: () => {} })}

        {chips && chips.length > 0 && (
          <CalendarChips
            chips={chips}
            activeChip={activeChip}
            onChipSelect={handleChipSelect}
            themeMode={themeMode}
            themeProductContext={themeProductContext}
          />
        )}

        <Box flexDirection="row" gap={16}>
          <Box flex={1}>
            <CalendarHeader
              monthDate={leftMonth}
              decreaseMonth={decreaseLeftMonth}
              increaseMonth={increaseLeftMonth}
              changeYear={changeLeftYear}
              changeMonth={changeLeftMonth}
              locale={locale}
              minDate={minDate}
              maxDate={maxDate}
              contextMenuMaxHeight={contextMenuMaxHeight}
              themeMode={themeMode}
              themeProductContext={themeProductContext}
            />

            <CalendarGrid
              currentMonth={leftMonth}
              locale={locale}
              firstDayOfWeek={firstDayOfWeek}
              selectsRange
              minDate={minDate}
              maxDate={maxDate}
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              selectingRange={selectingRange}
              onDayPress={handleDayPress}
              themeMode={themeMode}
              themeProductContext={themeProductContext}
            />
          </Box>

          <Box flex={1}>
            <CalendarHeader
              monthDate={rightMonth}
              decreaseMonth={decreaseRightMonth}
              increaseMonth={increaseRightMonth}
              changeYear={changeRightYear}
              changeMonth={changeRightMonth}
              locale={locale}
              minDate={minDate}
              maxDate={maxDate}
              contextMenuMaxHeight={contextMenuMaxHeight}
              themeMode={themeMode}
              themeProductContext={themeProductContext}
            />

            <CalendarGrid
              currentMonth={rightMonth}
              locale={locale}
              firstDayOfWeek={firstDayOfWeek}
              selectsRange
              minDate={minDate}
              maxDate={maxDate}
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              selectingRange={selectingRange}
              onDayPress={handleDayPress}
              themeMode={themeMode}
              themeProductContext={themeProductContext}
            />
          </Box>
        </Box>

        {bottomContent?.({ close: () => {} })}
      </Box>
    );
  }
);

DualCalendar.displayName = "DualCalendar";
